import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/site/Brand";
import { scrollToId } from "@/components/site/SmoothScroll";
import { JOIN } from "@/data/content";

const JOIN_ITEMS = JOIN.map((j) => ({ id: j.type, label: j.label, menu: j.menu, to: `/join/${j.type}` }));

const LINKS = [
  { id: "services", label: "Services" },
  { id: "how", label: "How it works" },
  { id: "contact", label: "Contact" },
];

const useSectionNav = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    if (pathname !== "/") {
      nav("/#" + id);
      setTimeout(() => scrollToId(id), 350);
    } else scrollToId(id);
  };
};

const DropMenu = ({ label, items, testid, primary }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button data-testid={testid} onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${primary ? "text-white bg-[#14083E] hover:bg-[#E5157A]" : "text-[#14083E] hover:bg-[#14083E]/5"}`}>
        {label} <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.22 }} className="absolute right-0 top-full pt-3 w-80" data-testid={`${testid}-menu`}>
            <div className="rounded-2xl bg-white shadow-[0_30px_60px_-20px_rgba(20,8,62,0.35)] border border-[#14083E]/10 p-2">
              {items.map((j) => (
                <Link key={j.id} to={j.to} data-testid={`nav-${j.id}-link`} onClick={() => setOpen(false)}
                  className="group flex items-start gap-3 rounded-xl p-3 hover:bg-[#FAF0F6] transition-colors">
                  <span className="mt-1 h-2 w-2 rounded-full bg-brand-gradient shrink-0" />
                  <span className="flex-1">
                    <span className="flex items-center justify-between font-display font-semibold text-[#14083E]">
                      {j.label}
                      <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] text-[#E5157A]" />
                    </span>
                    <span className="block text-xs text-slate-600 mt-0.5 leading-snug">{j.menu}</span>
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileMenu = ({ onClose, go }) => (
  <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
    className="lg:hidden absolute left-3 right-3 top-[calc(100%+8px)] rounded-3xl bg-white shadow-2xl border border-[#14083E]/10 p-5 max-h-[80vh] overflow-y-auto" data-lenis-prevent data-testid="mobile-menu">
    {LINKS.map((l) => (
      <button key={l.id} data-testid={`mobile-nav-${l.id}`} onClick={() => { go(l.id); onClose(); }}
        className="block w-full text-left py-3 font-display text-xl font-semibold text-[#14083E] border-b border-[#14083E]/10">{l.label}</button>
    ))}
    {[["Join Us", JOIN_ITEMS]].map(([title, items]) => (
      <div key={title}>
        <p className="font-mono-x text-[11px] uppercase tracking-[0.22em] text-[#c8106a] mt-5 mb-1">{title}</p>
        {items.map((j) => (
          <Link key={j.id} to={j.to} onClick={onClose} data-testid={`mobile-nav-${j.id}-link`}
            className="flex items-center justify-between py-2 text-[#14083E] font-medium">
            {j.label} <ArrowUpRight className="w-4 h-4 text-[#E5157A]" />
          </Link>
        ))}
      </div>
    ))}
  </motion.div>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const go = useSectionNav();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-6 pt-3" data-testid="site-header">
      <nav className={`relative mx-auto max-w-7xl flex items-center justify-between rounded-full pl-4 sm:pl-6 pr-2 py-2 transition-[background-color,box-shadow] duration-500 ${scrolled ? "bg-white/90 backdrop-blur-xl shadow-[0_10px_40px_-15px_rgba(20,8,62,0.35)]" : "bg-white/80 backdrop-blur-md"}`}>
        <Logo className="h-8 sm:h-10" />
        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <button key={l.id} data-testid={`nav-${l.id}`} onClick={() => go(l.id)}
              className="px-4 py-2 text-sm font-medium text-[#14083E] rounded-full hover:bg-[#14083E]/5 transition-colors">{l.label}</button>
          ))}
          <DropMenu label="Join Us" items={JOIN_ITEMS} testid="nav-join-us-dropdown" primary />
        </div>
        <button className="lg:hidden h-10 w-10 grid place-items-center rounded-full bg-[#14083E] text-white" data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} go={go} />}</AnimatePresence>
      </nav>
    </header>
  );
}
